import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { Modal, message } from "antd";
import {
  checkDeactivationBlockingRequestRoute,
  createReasonForBlockingRequestRoute,
  deactivateBlockingRequestRoute,
  deleteBlockingTypesRoute,
  editDateBlockingRequestRoute,
  getBlockingCurrentStateRoute,
  getBlockingCurrentStateVersionsRoute,
  getBlockingRequestSearchRoute,
  getBlockingTypesRoute,
  getBlockingTypesVersionsRoute,
  postBlockingRequestRoute,
  postBlockingTypesRoute,
  updateBlockingTypesRoute,
} from "../../lib/routes/blocking";
import { LoadingStatusCode } from "../../lib/types/LoadingStatusCode";
import loadingStatusCodes from "../../lib/const/loadingStatusCodes";
import {
  BlockingCurrentStateItem,
  BlockingRequestItem,
  BlockingTypesItem,
  CreateBlockingTypes,
} from "../../lib/interfaces/Blocking";
import { BlockingObjTypes } from "../../lib/const/blockingObjTypes";
import { AnyObject } from "../../lib/interfaces";

export type BlockingState = {
  blockingCurrentState: BlockingCurrentStateItem[];
  blockingCurrentStateLoading: LoadingStatusCode;

  blockingCurrentStateVersions: BlockingCurrentStateItem[];
  blockingCurrentStateVersionsLoading: LoadingStatusCode;

  blockingTypes: BlockingTypesItem[];
  blockingTypesLoading: LoadingStatusCode;

  blockingTypesVersions: BlockingTypesItem[];
  blockingTypesVersionsLoading: LoadingStatusCode;

  blockingRequest: BlockingRequestItem[];
  blockingRequestLoading: LoadingStatusCode;
};

const initialState: BlockingState = {
  blockingCurrentState: [],
  blockingCurrentStateLoading: loadingStatusCodes.idle,

  blockingCurrentStateVersions: [],
  blockingCurrentStateVersionsLoading: loadingStatusCodes.idle,

  blockingTypes: [],
  blockingTypesLoading: loadingStatusCodes.idle,

  blockingTypesVersions: [],
  blockingTypesVersionsLoading: loadingStatusCodes.idle,

  blockingRequest: [],
  blockingRequestLoading: loadingStatusCodes.idle,
};

const fetchBlockingCurrentState = createAsyncThunk(
  "blocking/fetchBlockingCurrentState",
  async ({
    objectTypeId,
    objectId,
    onDate,
  }: {
    objectTypeId: BlockingObjTypes;
    objectId: number | string;
    onDate?: string;
  }) => {
    const url = getBlockingCurrentStateRoute(objectTypeId, objectId, onDate);
    const { data } = await axios(url);
    return data;
  }
);

const fetchBlockingCurrentStateVersions = createAsyncThunk(
  "blocking/fetchBlockingCurrentStateVersions",
  async ({
    objectTypeId,
    objectId,
    onDate,
  }: {
    objectTypeId: BlockingObjTypes;
    objectId: number;
    onDate?: string;
  }) => {
    const url = getBlockingCurrentStateVersionsRoute(objectTypeId, objectId, onDate);
    const { data } = await axios(url);
    return data;
  }
);

const fetchBlockingRequest = createAsyncThunk(
  "blocking/fetchBlockingRequest",
  async ({
    objectTypeId,
    objectId,
    includeFutureDates,
    includePastDates,
  }: {
    objectTypeId: number;
    objectId: number | string;
    includeFutureDates: boolean;
    includePastDates: boolean;
  }) => {
    const url = getBlockingRequestSearchRoute(
      objectTypeId,
      objectId,
      includeFutureDates,
      includePastDates
    );
    const { data } = await axios(url);
    return data;
  }
);

const fetchBlockingTypes = createAsyncThunk(
  "blocking/fetchBlockingTypes",
  async (params: { onDate?: string } | undefined) => {
    const url = getBlockingTypesRoute(params);
    const { data } = await axios(url);
    return data;
  }
);

const fetchBlockingTypesVersions = createAsyncThunk(
  "blocking/fetchBlockingTypesVersions",
  async (blockingTypeId: number) => {
    const url = getBlockingTypesVersionsRoute(blockingTypeId);
    const { data } = await axios(url);
    return data;
  }
);

const createBlockingTypes = createAsyncThunk(
  "blocking/createBlockingTypes",
  async (params: { body: CreateBlockingTypes; onSuccess: () => void }) => {
    const errorCodes = {
      1001: `Ошибка создания Типа блокировки. Пожалуйста, повторите попытку позднее.`,
    };

    const url = postBlockingTypesRoute();
    const response = await axios
      .post(url, params.body)
      .then((res) => {
        message.destroy();
        message.success("Тип блокировки создан");
        params.onSuccess();
        return res;
      })
      .catch((err) => {
        const statusCode = err?.response?.status; // Получение статус-кода ошибки
        const statusMessage = err?.response?.data?.message; // Получение сообщения об ошибке
        const statusCodeError = err?.response?.data?.code; // Получение номера ошибки

        if (statusCode === 500) {
          if (statusCodeError === 1001) {
            Modal.error({
              content: errorCodes[statusCodeError],
              style: {
                maxWidth: 390,
              },
              onOk: params.onSuccess,
              closable: true,
            });
          } else {
            Modal.error({
              content: statusMessage,
              closable: true,
            });
          }
        }

        return err;
      });

    const { data } = response;

    return data;
  }
);

const deleteBlockingTypes = createAsyncThunk(
  "blocking/deleteBlockingTypes",
  async ({
    blockingTypeId,
    fromDate,
    onSuccess,
  }: {
    blockingTypeId: number;
    fromDate: string;
    onSuccess: () => void;
  }) => {
    const errorCodes = {
      1001: `Ошибка изменения даты закрытия. Пожалуйста, повторите попытку позднее.`,
    };

    const url = deleteBlockingTypesRoute(blockingTypeId, fromDate);
    const response = await axios
      .delete(url)
      .then((res) => {
        message.destroy();
        message.success("Дата закрытия изменена");
        onSuccess();
        return res;
      })
      .catch((err) => {
        const statusCode = err?.response?.status; // Получение статус-кода ошибки
        const statusMessage = err?.response?.data?.message; // Получение сообщения об ошибке
        const statusCodeError = err?.response?.data?.code; // Получение номера ошибки

        if (statusCode === 500) {
          if (statusCodeError === 1001) {
            Modal.error({
              content: errorCodes[statusCodeError],
              style: {
                maxWidth: 390,
              },
              onOk: onSuccess,
            });
          } else {
            Modal.error({
              content: statusMessage,
            });
          }
        }

        return err;
      });

    const { data } = response;

    return data;
  }
);

const updateBlockingTypes = createAsyncThunk(
  "blocking/updateBlockingTypes",
  async ({
    blockingTypeId,
    fromDate,
    body,
    onSuccess,
  }: {
    blockingTypeId: number;
    fromDate?: string;
    body: {};
    onSuccess: () => void;
  }) => {
    const errorCodes = {
      1001: `Ошибка редактирования Типа блокировки. Пожалуйста, повторите попытку позднее.`,
      UNEXPECTED_ERROR: `Ошибка редактирования Типа блокировки. Пожалуйста, повторите попытку позднее.`,
    };

    const url = updateBlockingTypesRoute(blockingTypeId, fromDate);
    const response = await axios
      .patch(url, body)
      .then((res) => {
        message.destroy();
        message.success("Тип блокировки изменен");
        onSuccess();
        return res;
      })
      .catch((err) => {
        const statusCode = err?.response?.status; // Получение статус-кода ошибки
        const statusMessage = err?.response?.data?.message; // Получение сообщения об ошибке
        const statusCodeError = err?.response?.data?.code; // Получение номера ошибки

        if (statusCode === 500) {
          if (errorCodes.hasOwnProperty(statusCodeError)) {
            Modal.error({
              content: errorCodes[statusCodeError],
              style: {
                maxWidth: 390,
              },
              onOk: onSuccess,
            });
          } else {
            Modal.error({
              content: statusMessage,
            });
          }
        }

        return err;
      });

    const { data } = response;

    return data;
  }
);

const createBlockingRequest = createAsyncThunk(
  "blocking/createBlockingRequest",
  async ({
    objectTypeId,
    objectId,
    body,
    onSuccess,
    onError,
    onClearLoading,
  }: {
    objectTypeId: number;
    objectId: number;
    body: AnyObject;
    onSuccess: () => void;
    onError: () => void;
    onClearLoading: () => void;
  }) => {
    const errorCodes = {
      1001: `Ошибка создания заявки. Пожалуйста, повторите попытку позднее.`,
    };

    const url = postBlockingRequestRoute(objectTypeId, objectId);
    const response = await axios
      .post(url, body)
      .then((res) => {
        message.destroy();
        message.success("Заявка создана");
        onSuccess();
        return res;
      })
      .catch((err) => {
        const statusCode = err?.response?.status; // Получение статус-кода ошибки
        const statusMessage = err?.response?.data?.message; // Получение сообщения об ошибке
        const statusCodeError = err?.response?.data?.code; // Получение номера ошибки

        if (statusCode === 500) {
          if (statusCodeError === 1001) {
            Modal.error({
              content: errorCodes[statusCodeError],
              style: {
                maxWidth: 390,
              },
              onOk: onError,
            });
          } else {
            Modal.error({
              content: statusMessage,
              onOk: onClearLoading,
            });
          }
        }

        return err;
      });

    const { data } = response;

    return data;
  }
);

const editBlockingRequest = createAsyncThunk(
  "blocking/editBlockingRequest",
  async ({
    requestId,
    fromDate,
    body,
    bodyReason,
    onSuccess,
    onError,
  }: {
    requestId: number;
    fromDate?: string;
    body: {};
    bodyReason: {};
    onSuccess: () => void;
    onError: () => void;
  }) => {
    const errorCodes = {
      1001: `Ошибка изменения периода заявки`,
    };

    const urlReason = createReasonForBlockingRequestRoute();
    const responseReason = await axios.post(urlReason, bodyReason);

    const reasonId = responseReason?.data?.id || null;
    const url = editDateBlockingRequestRoute(requestId, fromDate);
    const response = await axios
      .patch(url, { ...body, reasonId })
      .then((res) => {
        message.destroy();
        message.success("Период заявки изменен");
        onSuccess();
        return res;
      })
      .catch((err) => {
        const statusCode = err?.response?.status; // Получение статус-кода ошибки
        const statusMessage = err?.response?.data?.message; // Получение сообщения об ошибке
        const statusCodeError = err?.response?.data?.code; // Получение номера ошибки

        if (statusCode === 500) {
          if (statusCodeError === 1001) {
            Modal.error({
              content: errorCodes[statusCodeError],
              style: {
                maxWidth: 390,
              },
              onOk: onError,
            });
          } else {
            Modal.error({
              content: statusMessage,
              onOk: onError,
            });
          }
        }

        return err;
      });

    const { data } = response;

    return data;
  }
);

const deactivateBlockingRequest = createAsyncThunk(
  "blocking/deactivateBlockingRequest",
  async ({
    requestId,
    body,
    onSuccess,
    onError,
    onErrorForNotErrorCode,
  }: {
    requestId: number;
    body: {};
    onSuccess: () => void;
    onError: () => void;
    onErrorForNotErrorCode: () => void;
  }) => {
    const errorCodes = {
      1001: `Ошибка отмены заявки. Пожалуйста, повторите попытку позднее.`,
    };

    const url = deactivateBlockingRequestRoute(requestId);
    const response = await axios
      .post(url, body)
      .then((res) => {
        message.destroy();
        message.success("Заявка отменена");
        onSuccess();
        return res;
      })
      .catch((err) => {
        const statusCode = err?.response?.status; // Получение статус-кода ошибки
        const statusCodeError = err?.response?.data?.code; // Получение номера ошибки

        if (statusCode === 500) {
          if (statusCodeError === 1001) {
            Modal.error({
              content: errorCodes[statusCodeError],
              style: {
                maxWidth: 390,
              },
              onOk: onError,
            });
          } else {
            onErrorForNotErrorCode();
          }
        }

        return err;
      });

    const { data } = response;

    return data;
  }
);

const checkDeactivationBlockingRequest = createAsyncThunk(
  "blocking/checkDeactivationBlockingRequest",
  async ({
    requestId,
    onDate,
    onSuccess,
    onError,
  }: {
    requestId: number;
    onDate?: number;
    onSuccess: () => void;
    onError: () => void;
  }) => {
    const errorCodes = {
      1001: `Ошибка отмены заявки. Пожалуйста, повторите попытку позднее.`,
    };

    const url = checkDeactivationBlockingRequestRoute(requestId, onDate);
    const response = await axios
      .post(url)
      .then((res) => {
        if (
          res.data?.validationStatusId &&
          Array.isArray(res.data?.validationStatusId) &&
          res.data?.validationStatusId[0] === 100
        ) {
          message.destroy();
          onSuccess();
        } else {
          onError();
        }
        return res;
      })
      .catch((err) => {
        const statusCode = err?.response?.status; // Получение статус-кода ошибки
        const statusMessage = err?.response?.data?.message; // Получение сообщения об ошибке
        const statusCodeError = err?.response?.data?.code; // Получение номера ошибки

        if (statusCode === 500) {
          if (statusCodeError === 1001) {
            Modal.error({
              content: errorCodes[statusCodeError],
              style: {
                maxWidth: 390,
              },
              onOk: onError,
            });
          } else {
            Modal.error({
              content: statusMessage,
            });
          }
        }

        return err;
      });

    const { data } = response;

    return data;
  }
);

const slice = createSlice({
  name: "blocking",
  initialState,
  reducers: {
    resetBlockingModalData: (state) => {
      state.blockingCurrentState = [];
      state.blockingCurrentStateLoading = loadingStatusCodes.idle;
      state.blockingCurrentStateVersions = [];
      state.blockingCurrentStateVersionsLoading = loadingStatusCodes.idle;
      state.blockingRequest = [];
      state.blockingRequestLoading = loadingStatusCodes.idle;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBlockingCurrentState.fulfilled, (state, action) => {
        state.blockingCurrentState = action.payload;
        state.blockingCurrentStateLoading = loadingStatusCodes.fulfilled;
      })
      .addCase(fetchBlockingCurrentState.pending, (state) => {
        state.blockingCurrentStateLoading = loadingStatusCodes.pending;
      })
      .addCase(fetchBlockingCurrentState.rejected, (state) => {
        state.blockingCurrentStateLoading = loadingStatusCodes.rejected;
      })
      .addCase(fetchBlockingCurrentStateVersions.fulfilled, (state, action) => {
        state.blockingCurrentStateVersions = action.payload;
        state.blockingCurrentStateVersionsLoading = loadingStatusCodes.fulfilled;
      })
      .addCase(fetchBlockingCurrentStateVersions.pending, (state) => {
        state.blockingCurrentStateVersionsLoading = loadingStatusCodes.pending;
      })
      .addCase(fetchBlockingCurrentStateVersions.rejected, (state) => {
        state.blockingCurrentStateVersionsLoading = loadingStatusCodes.rejected;
      })
      .addCase(fetchBlockingTypes.fulfilled, (state, action) => {
        state.blockingTypes = action.payload;
        state.blockingTypesLoading = loadingStatusCodes.fulfilled;
      })
      .addCase(fetchBlockingTypes.pending, (state) => {
        state.blockingTypesLoading = loadingStatusCodes.pending;
      })
      .addCase(fetchBlockingTypes.rejected, (state) => {
        state.blockingTypesLoading = loadingStatusCodes.rejected;
      })
      .addCase(fetchBlockingTypesVersions.fulfilled, (state, action) => {
        state.blockingTypesVersions = action.payload;
        state.blockingTypesVersionsLoading = loadingStatusCodes.fulfilled;
      })
      .addCase(fetchBlockingTypesVersions.pending, (state) => {
        state.blockingTypesVersionsLoading = loadingStatusCodes.pending;
      })
      .addCase(fetchBlockingTypesVersions.rejected, (state) => {
        state.blockingTypesVersionsLoading = loadingStatusCodes.rejected;
      })

      .addCase(fetchBlockingRequest.fulfilled, (state, action) => {
        state.blockingRequest = action.payload;
        state.blockingRequestLoading = loadingStatusCodes.fulfilled;
      })
      .addCase(fetchBlockingRequest.pending, (state) => {
        state.blockingRequestLoading = loadingStatusCodes.pending;
      })
      .addCase(fetchBlockingRequest.rejected, (state) => {
        state.blockingRequestLoading = loadingStatusCodes.rejected;
      });
  },
});

const { actions, reducer } = slice;

const { resetBlockingModalData } = actions;

export {
  actions,
  fetchBlockingCurrentState,
  fetchBlockingCurrentStateVersions,
  fetchBlockingRequest,
  fetchBlockingTypes,
  fetchBlockingTypesVersions,
  createBlockingTypes,
  deleteBlockingTypes,
  updateBlockingTypes,
  createBlockingRequest,
  editBlockingRequest,
  deactivateBlockingRequest,
  checkDeactivationBlockingRequest,
  resetBlockingModalData,
};
export default reducer;
